import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVideo,
  faArrowRight,
  faArrowUpRightFromSquare,
  faBook,
  faPlayCircle,
  faStickyNote,
  faTriangleExclamation,
  faCircleCheck,
  faCircleInfo,
  faXmark,
  faClock,
  faPlus,
  faEye,
  faCopy,
  faUsers,
  faWrench,
  faBell,
  faHourglassHalf,
  faCalendarDay,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { useDemoProjects, demoForCourse } from '../components/demoProjects';
import './Classes.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const CLASS_MIN = 60;
const JOIN_EARLY_MIN = 5;

// Same issue codes as before (0 = resolved)
const ISSUES = [
  { id: 1, label: 'Student not joined' },
  { id: 2, label: 'Student left' },
  { id: 3, label: 'Internet issue' },
  { id: 4, label: 'Mic / audio issue' },
  { id: 5, label: 'Zoom link issue' },
];

// Same Zoom host accounts as before
const ZOOM_USERS = [
  { number: 1, email: 'robomind.lb@gmail.com', color: '#FF6B6B' },
  { number: 2, email: 'robomind.edu2@gmail.com', color: '#4ECDC4' },
  { number: 3, email: 'robomind.edu3@gmail.com', color: '#45B7D1' },
  { number: 4, email: 'robomind.edu4@gmail.com', color: '#96CEB4' },
  { number: 5, email: 'robokids.lb@gmail.com', color: '#FFEAA7' },
  { number: 6, email: 'robomind.lb.edu@gmail.com', color: '#DDA0DD' },
];
const EMAIL_TO_USER = Object.fromEntries(ZOOM_USERS.map((u) => [u.email, u.number]));
const userColor = (n) => ZOOM_USERS.find((u) => u.number === n)?.color || '#6c757d';
// readable text on any badge colour
const inkOn = (hex) => {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#0A1230' : '#FFFFFF';
};

/* Same date as before: the day in LEBANON time (old code added +3h to UTC). */
const beirutDate = (offsetDays) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Beirut', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(Date.now() + offsetDays * 86400000)
  );
const dayToDate = (day) => {
  const v = (day || '').toLowerCase();
  if (v === 'today') return beirutDate(0);
  if (v === 'tomorrow') return beirutDate(1);
  if (v === 'yesterday') return beirutDate(-1);
  return day;
};

const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
const digits = (phone) => String(phone || '').replace(/\D/g, '');
const waUrl = (phone, text) =>
  `https://api.whatsapp.com/send/?phone=${digits(phone)}${text ? `&text=${encodeURIComponent(text)}` : ''}&type=phone_number&app_absent=0`;

/* ---------- Flag from the time zone ----------
   Used when the server sends no country code (e.g. demo classes).
   Add a line if a flag is missing. */
// "Zone=countrycode;..." — every zone in the IANA tz database (zone.tab) + old names
const TZ_TABLE =
  'Africa/Abidjan=ci;Africa/Accra=gh;Africa/Addis_Ababa=et;Africa/Algiers=dz;Africa/Asmara=er;Africa/Asmera=er;Africa/Bamako=ml;Africa/Bangui=cf;Africa/Banjul=gm;Africa/Bissau=gw;Africa/Blantyre=mw;Africa/Brazzaville=cg;Africa/Bujumbura=bi;Africa/Cairo=eg;Africa/Casablanca=ma;Africa/Ceuta=es;Africa/Conakry=gn;Africa/Dakar=sn;Africa/Dar_es_Salaam=tz;Africa/Djibouti=dj;Africa/Douala=cm;Africa/El_Aaiun=eh;Africa/Freetown=sl;Africa/Gaborone=bw;Africa/Harare=zw;Africa/Johannesburg=za;Africa/Juba=ss;Africa/Kampala=ug;Africa/Khartoum=sd;Africa/Kigali=rw;Africa/Kinshasa=cd;Africa/Lagos=ng;Africa/Libreville=ga;Africa/Lome=tg;Africa/Luanda=ao;Africa/Lubumbashi=cd;Africa/Lusaka=zm;Africa/Malabo=gq;Africa/Maputo=mz;Africa/Maseru=ls;Africa/Mbabane=sz;Africa/Mogadishu=so;Africa/Monrovia=lr;Africa/Nairobi=ke;Africa/Ndjamena=td;Africa/Niamey=ne;Africa/Nouakchott=mr;Africa/Ouagadougou=bf;Africa/Porto-Novo=bj;Africa/Sao_Tome=st;Africa/Tripoli=ly;Africa/Tunis=tn;Africa/Windhoek=na;America/Adak=us;America/Anchorage=us;America/Anguilla=ai;America/Antigua=ag;America/Araguaina=br;America/Argentina/Buenos_Aires=ar;America/Argentina/Catamarca=ar;America/Argentina/Cordoba=ar;America/Argentina/Jujuy=ar;America/Argentina/La_Rioja=ar;America/Argentina/Mendoza=ar;America/Argentina/Rio_Gallegos=ar;America/Argentina/Salta=ar;America/Argentina/San_Juan=ar;America/Argentina/San_Luis=ar;America/Argentina/Tucuman=ar;America/Argentina/Ushuaia=ar;America/Aruba=aw;America/Asuncion=py;America/Atikokan=ca;America/Bahia=br;America/Bahia_Banderas=mx;America/Barbados=bb;America/Belem=br;America/Belize=bz;America/Blanc-Sablon=ca;America/Boa_Vista=br;America/Bogota=co;America/Boise=us;America/Buenos_Aires=ar;America/Cambridge_Bay=ca;America/Campo_Grande=br;America/Cancun=mx;America/Caracas=ve;America/Cayenne=gf;America/Cayman=ky;America/Chicago=us;America/Chihuahua=mx;America/Ciudad_Juarez=mx;America/Coral_Harbour=ca;America/Costa_Rica=cr;America/Coyhaique=cl;America/Creston=ca;America/Cuiaba=br;America/Curacao=cw;America/Danmarkshavn=gl;America/Dawson=ca;America/Dawson_Creek=ca;America/Denver=us;America/Detroit=us;America/Dominica=dm;America/Edmonton=ca;America/Eirunepe=br;America/El_Salvador=sv;America/Fort_Nelson=ca;America/Fortaleza=br;America/Glace_Bay=ca;America/Godthab=gl;America/Goose_Bay=ca;America/Grand_Turk=tc;America/Grenada=gd;America/Guadeloupe=gp;America/Guatemala=gt;America/Guayaquil=ec;America/Guyana=gy;America/Halifax=ca;America/Havana=cu;America/Hermosillo=mx;America/Indiana/Indianapolis=us;America/Indiana/Knox=us;America/Indiana/Marengo=us;America/Indiana/Petersburg=us;America/Indiana/Tell_City=us;America/Indiana/Vevay=us;America/Indiana/Vincennes=us;America/Indiana/Winamac=us;America/Inuvik=ca;America/Iqaluit=ca;America/Jamaica=jm;America/Juneau=us;America/Kentucky/Louisville=us;America/Kentucky/Monticello=us;America/Kralendijk=bq;America/La_Paz=bo;America/Lima=pe;America/Los_Angeles=us;America/Lower_Princes=sx;America/Maceio=br;America/Managua=ni;America/Manaus=br;America/Marigot=mf;America/Martinique=mq;America/Matamoros=mx;America/Mazatlan=mx;America/Menominee=us;America/Merida=mx;America/Metlakatla=us;America/Mexico_City=mx;America/Miquelon=pm;America/Moncton=ca;America/Monterrey=mx;America/Montevideo=uy;America/Montreal=ca;America/Montserrat=ms;America/Nassau=bs;America/New_York=us;America/Nome=us;America/Noronha=br;America/North_Dakota/Beulah=us;America/North_Dakota/Center=us;America/North_Dakota/New_Salem=us;America/Nuuk=gl;America/Ojinaga=mx;America/Panama=pa;America/Paramaribo=sr;America/Phoenix=us;America/Port-au-Prince=ht;America/Port_of_Spain=tt;America/Porto_Velho=br;America/Puerto_Rico=pr;America/Punta_Arenas=cl;America/Rankin_Inlet=ca;America/Recife=br;America/Regina=ca;America/Resolute=ca;America/Rio_Branco=br;America/Santarem=br;America/Santiago=cl;America/Santo_Domingo=do;America/Sao_Paulo=br;America/Scoresbysund=gl;America/Sitka=us;America/St_Barthelemy=bl;America/St_Johns=ca;America/St_Kitts=kn;America/St_Lucia=lc;America/St_Thomas=vi;America/St_Vincent=vc;America/Swift_Current=ca;America/Tegucigalpa=hn;America/Thule=gl;America/Tijuana=mx;America/Toronto=ca;America/Tortola=vg;America/Vancouver=ca;America/Whitehorse=ca;America/Winnipeg=ca;America/Yakutat=us;Antarctica/Casey=aq;Antarctica/Davis=aq;Antarctica/DumontDUrville=aq;Antarctica/Macquarie=au;Antarctica/Mawson=aq;Antarctica/McMurdo=aq;Antarctica/Palmer=aq;Antarctica/Rothera=aq;Antarctica/Syowa=aq;Antarctica/Troll=aq;Antarctica/Vostok=aq;Arctic/Longyearbyen=sj;Asia/Aden=ye;Asia/Almaty=kz;Asia/Amman=jo;Asia/Anadyr=ru;Asia/Aqtau=kz;Asia/Aqtobe=kz;Asia/Ashgabat=tm;Asia/Atyrau=kz;Asia/Baghdad=iq;Asia/Bahrain=bh;Asia/Baku=az;Asia/Bangkok=th;Asia/Barnaul=ru;Asia/Beirut=lb;Asia/Bishkek=kg;Asia/Brunei=bn;Asia/Calcutta=in;Asia/Chita=ru;Asia/Chongqing=cn;Asia/Colombo=lk;Asia/Dacca=bd;Asia/Damascus=sy;Asia/Dhaka=bd;Asia/Dili=tl;Asia/Dubai=ae;Asia/Dushanbe=tj;Asia/Famagusta=cy;Asia/Gaza=ps;Asia/Harbin=cn;Asia/Hebron=ps;Asia/Ho_Chi_Minh=vn;Asia/Hong_Kong=hk;Asia/Hovd=mn;Asia/Irkutsk=ru;Asia/Istanbul=tr;Asia/Jakarta=id;Asia/Jayapura=id;Asia/Jerusalem=il;Asia/Kabul=af;Asia/Kamchatka=ru;Asia/Karachi=pk;Asia/Kathmandu=np;Asia/Katmandu=np;Asia/Khandyga=ru;Asia/Kolkata=in;Asia/Krasnoyarsk=ru;Asia/Kuala_Lumpur=my;Asia/Kuching=my;Asia/Kuwait=kw;Asia/Macao=mo;Asia/Macau=mo;Asia/Magadan=ru;Asia/Makassar=id;Asia/Manila=ph;Asia/Muscat=om;Asia/Nicosia=cy;Asia/Novokuznetsk=ru;Asia/Novosibirsk=ru;Asia/Omsk=ru;Asia/Oral=kz;Asia/Phnom_Penh=kh;Asia/Pontianak=id;Asia/Pyongyang=kp;Asia/Qatar=qa;Asia/Qostanay=kz;Asia/Qyzylorda=kz;Asia/Rangoon=mm;Asia/Riyadh=sa;Asia/Saigon=vn;Asia/Sakhalin=ru;Asia/Samarkand=uz;Asia/Seoul=kr;Asia/Shanghai=cn;Asia/Singapore=sg;Asia/Srednekolymsk=ru;Asia/Taipei=tw;Asia/Tashkent=uz;Asia/Tbilisi=ge;Asia/Tehran=ir;Asia/Tel_Aviv=il;Asia/Thimbu=bt;Asia/Thimphu=bt;Asia/Tokyo=jp;Asia/Tomsk=ru;Asia/Ujung_Pandang=id;Asia/Ulaanbaatar=mn;Asia/Ulan_Bator=mn;Asia/Urumqi=cn;Asia/Ust-Nera=ru;Asia/Vientiane=la;Asia/Vladivostok=ru;Asia/Yakutsk=ru;Asia/Yangon=mm;Asia/Yekaterinburg=ru;Asia/Yerevan=am;Atlantic/Azores=pt;Atlantic/Bermuda=bm;Atlantic/Canary=es;Atlantic/Cape_Verde=cv;Atlantic/Faeroe=fo;Atlantic/Faroe=fo;Atlantic/Madeira=pt;Atlantic/Reykjavik=is;Atlantic/South_Georgia=gs;Atlantic/St_Helena=sh;Atlantic/Stanley=fk;Australia/ACT=au;Australia/Adelaide=au;Australia/Brisbane=au;Australia/Broken_Hill=au;Australia/Darwin=au;Australia/Eucla=au;Australia/Hobart=au;Australia/Lindeman=au;Australia/Lord_Howe=au;Australia/Melbourne=au;Australia/NSW=au;Australia/Perth=au;Australia/Sydney=au;Canada/Eastern=ca;Canada/Pacific=ca;Egypt=eg;Europe/Amsterdam=nl;Europe/Andorra=ad;Europe/Astrakhan=ru;Europe/Athens=gr;Europe/Belfast=gb;Europe/Belgrade=rs;Europe/Berlin=de;Europe/Bratislava=sk;Europe/Brussels=be;Europe/Bucharest=ro;Europe/Budapest=hu;Europe/Busingen=de;Europe/Chisinau=md;Europe/Copenhagen=dk;Europe/Dublin=ie;Europe/Gibraltar=gi;Europe/Guernsey=gg;Europe/Helsinki=fi;Europe/Isle_of_Man=im;Europe/Istanbul=tr;Europe/Jersey=je;Europe/Kaliningrad=ru;Europe/Kiev=ua;Europe/Kirov=ru;Europe/Kyiv=ua;Europe/Lisbon=pt;Europe/Ljubljana=si;Europe/London=gb;Europe/Luxembourg=lu;Europe/Madrid=es;Europe/Malta=mt;Europe/Mariehamn=ax;Europe/Minsk=by;Europe/Monaco=mc;Europe/Moscow=ru;Europe/Nicosia=cy;Europe/Oslo=no;Europe/Paris=fr;Europe/Podgorica=me;Europe/Prague=cz;Europe/Riga=lv;Europe/Rome=it;Europe/Samara=ru;Europe/San_Marino=sm;Europe/Sarajevo=ba;Europe/Saratov=ru;Europe/Simferopol=ua;Europe/Skopje=mk;Europe/Sofia=bg;Europe/Stockholm=se;Europe/Tallinn=ee;Europe/Tirane=al;Europe/Ulyanovsk=ru;Europe/Vaduz=li;Europe/Vatican=va;Europe/Vienna=at;Europe/Vilnius=lt;Europe/Volgograd=ru;Europe/Warsaw=pl;Europe/Zagreb=hr;Europe/Zurich=ch;GB=gb;Hongkong=hk;Indian/Antananarivo=mg;Indian/Chagos=io;Indian/Christmas=cx;Indian/Cocos=cc;Indian/Comoro=km;Indian/Kerguelen=tf;Indian/Mahe=sc;Indian/Maldives=mv;Indian/Mauritius=mu;Indian/Mayotte=yt;Indian/Reunion=re;Iran=ir;Israel=il;Japan=jp;Kwajalein=mh;NZ=nz;PRC=cn;Pacific/Apia=ws;Pacific/Auckland=nz;Pacific/Bougainville=pg;Pacific/Chatham=nz;Pacific/Chuuk=fm;Pacific/Easter=cl;Pacific/Efate=vu;Pacific/Fakaofo=tk;Pacific/Fiji=fj;Pacific/Funafuti=tv;Pacific/Galapagos=ec;Pacific/Gambier=pf;Pacific/Guadalcanal=sb;Pacific/Guam=gu;Pacific/Honolulu=us;Pacific/Kanton=ki;Pacific/Kiritimati=ki;Pacific/Kosrae=fm;Pacific/Kwajalein=mh;Pacific/Majuro=mh;Pacific/Marquesas=pf;Pacific/Midway=um;Pacific/Nauru=nr;Pacific/Niue=nu;Pacific/Norfolk=nf;Pacific/Noumea=nc;Pacific/Pago_Pago=as;Pacific/Palau=pw;Pacific/Pitcairn=pn;Pacific/Pohnpei=fm;Pacific/Ponape=fm;Pacific/Port_Moresby=pg;Pacific/Rarotonga=ck;Pacific/Saipan=mp;Pacific/Tahiti=pf;Pacific/Tarawa=ki;Pacific/Tongatapu=to;Pacific/Truk=fm;Pacific/Wake=um;Pacific/Wallis=wf;Poland=pl;Portugal=pt;ROC=tw;ROK=kr;Singapore=sg;Turkey=tr;US/Central=us;US/Eastern=us;US/Mountain=us;US/Pacific=us';
const TZ_COUNTRY = Object.fromEntries(TZ_TABLE.split(';').map((p) => p.split('=')));
// whole-word match, so a short city like "Male" doesn't match inside "Guatemala"
const hasWord = (text, word) => word.length > 2 && new RegExp(`(^|[^a-z])${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`).test(text);
const tzCityKey = (s) => String(s || '').split('/').pop().replace(/[_\s]+/g, ' ').trim().toLowerCase();
const countryFromTz = (tz) => {
  if (!tz) return '';
  if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
  const city = tzCityKey(tz);
  let key = Object.keys(TZ_COUNTRY).find((k) => tzCityKey(k) === city);
  if (!key) {
    const text = String(tz).toLowerCase(); // e.g. "(GMT+03:00) Beirut"
    key = Object.keys(TZ_COUNTRY).find((k) => hasWord(text, tzCityKey(k)));
  }
  return key ? TZ_COUNTRY[key] : '';
};
const flagOf = (code, tz) => {
  const c = String(code || '').trim();
  if (c.length === 2 && c.toUpperCase() !== 'UN') return c.toLowerCase();
  return countryFromTz(tz);
};

/* ---------- Student's own time + city (same as before) ---------- */
const validTz = (tz) => tz && tz !== 'Unknown' && !tz.startsWith('Etc/GMT');
const studentTime = (date, tz) => {
  try {
    return validTz(tz) ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: tz }) : fmtTime(date);
  } catch {
    return fmtTime(date);
  }
};
const tzCity = (tz) => (validTz(tz) ? tz.split('/').pop().replace(/_/g, ' ') : '');

/* ---------- WhatsApp reminders (same texts as before) ---------- */
const TEMPLATES = [
  (n, t) => `Hi ${n}! Just a kind Reminder that Your Session is Today at ${t}. We're excited to see you! 🚀 Robomind Team`,
  (n, t) => `Hello ${n}, Your Coding Session is Scheduled for ${t}. Get ready to learn and have fun! 🚀 Robomind Team`,
  (n, t) => `Hey ${n}! Don't Forget Your Class at ${t} today. Let's build something awesome together! 🚀 Robomind Team`,
  (n, t) => `Dear ${n}, This is a Friendly Reminder for Your Session at ${t}. Looking forward to another great lesson! 🚀 Robomind Team`,
  (n, t) => `Hi ${n}, Hope you're doing Great! Your session is Today at ${t}. Let's code and have fun! 🚀 Robomind Team`,
];
const reminderText = (cls, late) => {
  if (late) return `Hello ${cls.firstName}, Your Session Has Already Started. We're Waiting for You to Join! 🚀 Robomind Team`;
  const city = tzCity(cls.timezone);
  const t = `${studentTime(cls.classDateTime, cls.timezone)}${city ? ` Time ${city}` : ''}`;
  // every reminder asks the parent to confirm (WhatsApp shows _text_ in italic)
  return `${TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)](cls.firstName, t)}\n\n_Send 👍 to confirm_`;
};

/* ---------- Conflicts (same rules as before) ---------- */
const overlaps = (a0, a1, b0, b1) => a0 < b1 && b0 < a1;

const bestUserFor = (cls, slotsByUser) => {
  const start = cls.classDateTime;
  const end = new Date(start.getTime() + CLASS_MIN * 60000);
  let best = null;
  let bestScore = Infinity;
  ZOOM_USERS.forEach((u) => {
    if (u.number === cls.userNumber) return;
    let direct = false;
    let score = 0;
    (slotsByUser[u.number] || []).forEach((s) => {
      if (overlaps(start, end, s.start, s.end)) {
        direct = true;
        score += 100;
      }
      if (s.end <= start) {
        const gap = (start - s.end) / 60000;
        if (gap < 30) score += 30 - gap;
      }
      if (s.start >= end) {
        const gap = (s.start - end) / 60000;
        if (gap < 30) score += 30 - gap;
      }
    });
    if (!direct && score < bestScore) {
      bestScore = score;
      best = u;
    }
  });
  if (!best) {
    let min = Infinity;
    ZOOM_USERS.forEach((u) => {
      if (u.number === cls.userNumber) return;
      const n = (slotsByUser[u.number] || []).filter((s) => overlaps(start, end, s.start, s.end)).length;
      if (n < min) {
        min = n;
        best = u;
      }
    });
  }
  return best;
};

const analyse = (list) => {
  const slotsByUser = {};
  list.forEach((c) => {
    if (!c.userNumber) return;
    (slotsByUser[c.userNumber] = slotsByUser[c.userNumber] || []).push({
      id: c.id,
      name: c.studentName,
      start: c.classDateTime,
      end: new Date(c.classDateTime.getTime() + CLASS_MIN * 60000),
    });
  });

  let delayRisk = 0;
  const out = list.map((c) => {
    if (!c.userNumber) return c;
    const start = c.classDateTime;
    const end = new Date(start.getTime() + CLASS_MIN * 60000);
    const others = (slotsByUser[c.userNumber] || []).filter((s) => s.id !== c.id);

    const clash = others.filter((s) => overlaps(start, end, s.start, s.end));
    if (clash.length) {
      const s = clash[clash.length - 1];
      return {
        ...c,
        conflict: true,
        conflictMessage: `Overlaps with ${s.name} (${fmtTime(s.start)} – ${fmtTime(s.end)})`,
        suggestion: bestUserFor(c, slotsByUser),
      };
    }
    const near = others.find((s) => Math.abs(start - s.end) < 30 * 60000 || Math.abs(s.start - end) < 30 * 60000);
    if (near) {
      delayRisk += 1;
      const gap = Math.round(Math.min(Math.abs(start - near.end), Math.abs(near.start - end)) / 60000);
      return {
        ...c,
        potentialConflict: true,
        conflictMessage: `Close to ${near.name} (${gap} min gap at ${fmtTime(near.end)})`,
        suggestion: bestUserFor(c, slotsByUser),
      };
    }
    return c;
  });

  const stats = { total: out.length, fixed: 0, needFix: 0, needCreate: 0, conflicts: 0, delayRisk };
  out.forEach((c) => {
    if (!c.hasZoomLink) stats.needCreate += 1;
    else if (c.meetingTimeMatch) stats.fixed += 1;
    else stats.needFix += 1;
    if (c.conflict) stats.conflicts += 1;
  });
  return { list: out, stats };
};

/* ======================================================================
   Component
   ====================================================================== */
const Classes = ({ day, userData }) => {
  const [classes, setClasses] = useState([]);
  const demos = useDemoProjects('admin'); // demo made for the course the parent asked for
  const [stats, setStats] = useState({ total: 0, fixed: 0, needFix: 0, needCreate: 0, conflicts: 0, delayRisk: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState({}); // { [key]: true }
  const [fixingAll, setFixingAll] = useState(false);
  const [dialog, setDialog] = useState(null); // { tone, title, body, actions }
  const requestRef = useRef(0);

  const adminId = userData?.id;
  const setBusyKey = (key, on) =>
    setBusy((b) => {
      const n = { ...b };
      if (on) n[key] = true;
      else delete n[key];
      return n;
    });

  // ---------- Load (same endpoints), refresh every minute ----------
  const fetchClasses = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      const res = await axios.get(`${API}/api/admin/classes/${dayToDate(day)}`, auth());
      const rows = await Promise.all(
        (res.data?.myClasses || []).map(async (cls) => {
          let zoom = null;
          let userNumber = null;
          if (cls.ZoomLink) {
            try {
              const z = await axios.post(`${API}/api/admin/zoominfo/meeting-info`, { zoomLink: cls.ZoomLink }, { ...auth(), timeout: 10000 });
              if (z.data?.success) {
                zoom = z.data.meeting;
                const email = (zoom?.host_email || '').toLowerCase();
                if (EMAIL_TO_USER[email]) userNumber = EMAIL_TO_USER[email];
                else {
                  const m = email.match(/robomind\.edu(\d+)@/i);
                  if (m) userNumber = parseInt(m[1], 10);
                }
              }
            } catch (err) {
              console.error('Error fetching Zoom info:', err.message);
            }
          }
          const classDateTime = new Date(cls.ClassDateTime);
          const taken = parseInt(cls.sessionsTaken, 10) || 0;
          const total = parseInt(cls.totalSessions, 10) || 0;
          return {
            id: cls.Id,
            type: cls.Type === 0 ? 'paid' : 'demo',
            IdUser: cls.IdUser,
            studentImage: cls.image || '',
            studentName: `${cls.FirstName || ''} ${cls.LastName || ''}`.trim(),
            teacherName: `${cls.TFirstName || ''} ${cls.TLastName || ''}`.trim(),
            firstName: cls.FirstName || '',
            MobileNumber: cls.MobileNumber,
            StudentJoin: cls.StudentJoin,
            TeacherJoin: cls.TeacherJoin,
            classesTaken: `${taken}/${total}`,
            isLastClass: taken === total,
            hasPTM: taken === total,
            hasFinish: taken > total,
            level: cls.IdGradesPlans === 2 ? 'Junior' : cls.IdGradesPlans === 1 ? 'Little' : 'Group',
            Awaiting: cls.Awaiting > 0,
            countryCode: cls.countryCode || 'UN',
            timezone: cls.timezone || cls.Timezone || 'Unknown',
            flag: flagOf(cls.countryCode, cls.timezone || cls.Timezone), // demo classes: from the time zone
            prevProject: cls.lastCourse?.CourseName || 'No previous project',
            nextProject: cls.nextCourse?.CourseName || 'No next project',
            classDateTime,
            plan: cls.IdGradesPlans,
            course: cls.InterestedCourse || '', // demo: course the parent asked for ('' = not sure)
            courseNotes: cls.coursenotes,
            lastCourseLink: cls.lastCourse?.CourseLink,
            nextCourseLink: cls.nextCourse?.CourseLink,
            projectLink: cls.lastCourse?.CourseProjectLink,
            nextprojectLink: cls.nextCourse?.CourseProjectLink,
            Issue: cls.Issue ?? null,
            ZoomLink: cls.ZoomLink,
            hasZoomLink: Boolean(cls.ZoomLink),
            meetingTimeMatch: zoom?.start_time ? Math.abs(classDateTime - new Date(zoom.start_time)) / 60000 <= 5 : false,
            userNumber,
            hostEmail: zoom?.host_email || null,
            join_url: zoom?.join_url || null,
            password: zoom?.password || null,
            meetingId: zoom?.id || null,
          };
        })
      );
      if (requestId !== requestRef.current) return;
      const { list, stats: s } = analyse(rows);
      setClasses(list);
      setStats(s);
      setError('');
    } catch (err) {
      console.error('Error in fetchClasses:', err);
      if (requestId === requestRef.current) setError('We couldn’t load the classes.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [day]);

  useEffect(() => {
    if (!adminId) return undefined;
    setLoading(true);
    fetchClasses();
    const id = setInterval(fetchClasses, 60000);
    return () => clearInterval(id);
  }, [adminId, fetchClasses]);

  // keeps "Now", join + report windows current
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const info = (title, body, tone = 'info') => setDialog({ tone, title, body });

  /* ---------- Zoom actions (same endpoints + payloads) ---------- */
  const fixAll = async () => {
    setFixingAll(true);
    try {
      const res = await axios.post(`${API}/api/admin/zoom/fix-all-meetings`, { date: dayToDate(day) }, auth());
      if (res.data?.success) {
        const f = res.data.fixedCount || 0;
        const c = res.data.createdCount || 0;
        const t = res.data.totalClasses || 0;
        info(
          'Meetings checked',
          <ul className="rmacl-dl">
            <li><span>Fixed</span><strong>{f}</strong></li>
            <li><span>Created</span><strong>{c}</strong></li>
            <li><span>Classes checked</span><strong>{t}</strong></li>
            <li className="is-wide">{f || c ? 'Refresh your Zoom calendar to see the changes.' : 'All meetings were already correct.'}</li>
          </ul>,
          'success'
        );
        await fetchClasses();
      } else {
        info('Couldn’t fix the meetings', res.data?.error || 'Unknown error', 'error');
      }
    } catch (err) {
      console.error('Error fixing meetings:', err);
      info('Couldn’t fix the meetings', err.response?.data?.details || err.message, 'error');
    } finally {
      setFixingAll(false);
    }
  };

  const createMeeting = async (cls) => {
    setBusyKey(`create-${cls.IdUser}`, true);
    try {
      const res = await axios.post(
        `${API}/api/admin/zoom/create-meeting`,
        { studentName: cls.studentName, classDateTime: cls.classDateTime, duration: 60, teacherName: cls.teacherName, studentId: cls.IdUser },
        auth()
      );
      if (res.data?.success) {
        const m = res.data.meeting || {};
        info(
          'Zoom meeting created',
          <ul className="rmacl-dl">
            <li className="is-wide"><span>Link</span><a href={m.join_url} target="_blank" rel="noopener noreferrer">{m.join_url}</a></li>
            <li><span>Password</span><strong>{m.password || '123456'}</strong></li>
            <li><span>Zoom user</span><strong>U{m.userNumber}</strong></li>
            <li className="is-wide">Scheduled with cloud recording.</li>
          </ul>,
          'success'
        );
        await fetchClasses();
      } else {
        info('Couldn’t create the meeting', res.data?.error || 'Unknown error', 'error');
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      info('Couldn’t create the meeting', err.response?.data?.details || err.message, 'error');
    } finally {
      setBusyKey(`create-${cls.IdUser}`, false);
    }
  };

  const fixMeeting = async (cls) => {
    setBusyKey(`fix-${cls.id}`, true);
    try {
      const res = await axios.post(
        `${API}/api/admin/zoom/update-meeting-time`,
        { zoomLink: cls.ZoomLink, newStartTime: cls.classDateTime, duration: 60 },
        auth()
      );
      if (res.data?.success) {
        info('Meeting time fixed', `${cls.studentName}’s meeting now starts at ${fmtTime(cls.classDateTime)}.`, 'success');
        await fetchClasses();
      } else {
        info('Couldn’t fix the meeting', res.data?.error || 'Unknown error', 'error');
      }
    } catch (err) {
      console.error('Error fixing meeting:', err);
      info('Couldn’t fix the meeting', err.response?.data?.details || err.message, 'error');
    } finally {
      setBusyKey(`fix-${cls.id}`, false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setDialog((d) => (d ? { ...d, copied: label } : d));
    } catch {
      /* clipboard blocked */
    }
  };

  const showZoomInfo = (cls) => {
    const meetingId = cls.meetingId || 'N/A';
    const password = cls.password && cls.password !== 'N/A' ? cls.password : '123456';
    const joinUrl = cls.join_url || cls.ZoomLink || '';
    const text = `Meeting ID: ${meetingId}\nPassword: ${password}`;
    setDialog({
      tone: 'info',
      title: 'Meeting details',
      kind: 'zoom',
      body: (
        <ul className="rmacl-dl">
          <li><span>Meeting ID</span><strong>{meetingId}</strong></li>
          <li><span>Password</span><strong>{password}</strong></li>
          <li className="is-wide"><span>Link</span>{joinUrl ? <a href={joinUrl} target="_blank" rel="noopener noreferrer">{joinUrl}</a> : 'N/A'}</li>
        </ul>
      ),
      actions: [
        { label: 'Copy ID + password', icon: faCopy, onClick: () => copy(text, 'Copied') },
        cls.MobileNumber && { label: 'Send on WhatsApp', icon: faWhatsapp, href: waUrl(cls.MobileNumber, text), wa: true },
      ].filter(Boolean),
    });
  };

  const showSuggestion = (cls) => {
    const s = cls.suggestion;
    copy(s.email);
    setDialog({
      tone: cls.conflict ? 'error' : 'warn',
      title: `${cls.conflict ? 'Conflict' : 'Delay risk'}: ${cls.studentName}`,
      body: (
        <>
          <p className="rmacl-dialog-text">{cls.conflictMessage}</p>
          <ul className="rmacl-dl">
            <li><span>Now on</span><strong>U{cls.userNumber}</strong></li>
            <li><span>Move to</span><strong>U{s.number}</strong></li>
            <li className="is-wide"><span>Host email</span><strong>{s.email}</strong></li>
          </ul>
          <p className="rmacl-dialog-text">In Zoom, open the meeting and change the host to this email. It’s already copied.</p>
        </>
      ),
      actions: [{ label: 'Copy email again', icon: faCopy, onClick: () => copy(s.email, 'Copied') }],
    });
  };

  /* ---------- Join (same endpoint) ---------- */
  const join = async (cls) => {
    const start = cls.classDateTime.getTime();
    const live = Date.now() >= start - JOIN_EARLY_MIN * 60000 && Date.now() <= start + CLASS_MIN * 60000;
    const tab = live ? window.open('', '_blank') : null; // avoids popup blocking
    setBusyKey(`join-${cls.id}`, true);
    try {
      const res = await axios.post(`${API}/api/classes/check-availability`, { classId: cls.id }, auth());
      const { status, message, zoomUrl } = res.data || {};
      if (status === 'joining' && zoomUrl) {
        if (tab) tab.location.href = zoomUrl;
        else window.open(zoomUrl, '_blank');
        return;
      }
      tab?.close();
      if (status === 'expired') info('Class has ended', message, 'error');
      else if (status === 'early') info('Class not available yet', `${message}\nSession duration: 50 to 60 minutes.`, 'warn');
    } catch (err) {
      tab?.close();
      console.error('Error:', err);
      info('Something went wrong', 'Please try again.', 'error');
    } finally {
      setBusyKey(`join-${cls.id}`, false);
    }
  };

  /* ---------- Report issue (same endpoint + payload) ---------- */
  const report = async (cls, issue) => {
    setBusyKey(`issue-${cls.id}`, true);
    try {
      const res = await axios.post(`${API}/api/teacher/submitissue`, { IdSchedule: cls.id, IdIssue: issue }, auth());
      if (res.status === 200) await fetchClasses();
      else info('Report not sent', 'Please try again.', 'error');
    } catch (err) {
      console.error('Error submitting issue:', err);
      info('Report not sent', 'An error occurred while sending the report.', 'error');
    } finally {
      setBusyKey(`issue-${cls.id}`, false);
    }
  };

  /* ---------- Reminders (same messages) ---------- */
  // "Remind" sends one random template (the old button opened 5 WhatsApp tabs; browsers block all but the first)
  const remind = (cls, late) => window.open(waUrl(cls.MobileNumber, reminderText(cls, late)), '_blank');

  // Escape closes the dialog
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => e.key === 'Escape' && setDialog(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog]);

  /* ---------------- Render ---------------- */
  if (loading) {
    return (
      <div className="rmacl" aria-busy="true">
        <div className="rmacl-skel rmacl-skel--bar" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmacl-skel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmacl">
        <div className="rmacl-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmacl-btn" onClick={() => { setLoading(true); fetchClasses(); }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const work = stats.needFix > 0 || stats.needCreate > 0;
  const STAT = [
    ['total', 'Total', faCalendarDay, 'grey'],
    ['fixed', 'Fixed', faCircleCheck, 'green'],
    ['needFix', 'Need fix', faClock, 'amber'],
    ['needCreate', 'Need create', faPlus, 'blue'],
    ['conflicts', 'Conflicts', faTriangleExclamation, 'red'],
    ['delayRisk', 'Delay risk', faHourglassHalf, 'orange'],
  ];

  return (
    <div className="rmacl">
      {/* ---------- Zoom health ---------- */}
      <div className="rmacl-statbar">
        <ul className="rmacl-stats">
          {STAT.map(([k, label, icon, tone]) => (
            <li key={k} className={`tone-${tone} ${k !== 'total' && k !== 'fixed' && stats[k] > 0 ? 'is-alert' : ''}`}>
              <FontAwesomeIcon icon={icon} /> {label} <b>{stats[k]}</b>
            </li>
          ))}
        </ul>
        <button type="button" className="rmacl-btn" onClick={fixAll} disabled={fixingAll || !work} title={work ? 'Fix all meetings' : 'All meetings are already fixed'}>
          <FontAwesomeIcon icon={faWrench} /> {fixingAll ? 'Fixing…' : 'Fix all meetings'}
        </button>
      </div>

      {classes.length === 0 ? (
        <div className="rmacl-empty">
          <FontAwesomeIcon icon={faCalendarDay} />
          <p>No classes scheduled for this day.</p>
        </div>
      ) : (
        <ul className="rmacl-list">
          {classes.map((cls) => {
            const start = cls.classDateTime.getTime();
            const isLive = now >= start && now <= start + CLASS_MIN * 60000;
            const reportOpen = isLive;
            const minsTo = Math.round((start - now) / 60000);
            const city = tzCity(cls.timezone);
            const hostColor = userColor(cls.userNumber);
            const hasIssue = cls.Issue !== null && cls.Issue !== undefined && cls.Issue !== 0;
            // one Join button, shown in the projects row on desktop and under the time on phones
            const joinBtn = (where) => (
              <button type="button" className={`rmacl-join ${where}`} onClick={() => join(cls)} disabled={busy[`join-${cls.id}`]}>
                <FontAwesomeIcon icon={faVideo} /> Join <FontAwesomeIcon icon={faArrowRight} />
              </button>
            );

            return (
              <li key={cls.id} className={`rmacl-card is-${cls.type} ${isLive ? 'is-live' : ''} ${hasIssue && isLive ? 'has-issue' : ''}`}>
                {/* ---- top ---- */}
                <div className="rmacl-top">
                  <div className="rmacl-avatar-wrap">
                    <span className="rmacl-avatar">
                      {cls.studentImage ? <img src={cls.studentImage} alt="" /> : <FontAwesomeIcon icon={faUsers} />}
                    </span>
                    {cls.flag && (
                      <span className="rmacl-flag" title={cls.timezone}>
                        <span className={`fi fi-${cls.flag}`} />
                      </span>
                    )}
                  </div>

                  <div className="rmacl-who">
                    <h3>
                      {cls.studentName || 'Student'}
                      <a href={`/Admin/studentprofile/${cls.IdUser}`} target="_blank" rel="noopener noreferrer" aria-label="Open student profile">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </h3>
                    <p>Teacher: {cls.teacherName || '—'}</p>
                    <div className="rmacl-tags">
                      <span className={`rmacl-tag is-${cls.type}`}>{cls.type === 'paid' ? 'Paid' : 'Demo'}</span>
                      <span className="rmacl-tag">{cls.level}</span>
                      {cls.type === 'paid' && !cls.hasFinish && (
                        <span className={`rmacl-tag ${cls.isLastClass ? 'is-red' : ''}`}>Class {cls.classesTaken}</span>
                      )}
                      {cls.type === 'paid' && cls.hasPTM && <span className="rmacl-tag is-green">PTM</span>}
                      {cls.type === 'paid' && cls.Awaiting && <span className="rmacl-tag is-amber">Awaiting payment</span>}
                      {cls.type === 'paid' && cls.hasFinish && <span className="rmacl-tag is-blue">Awaiting updates</span>}
                    </div>
                    <div className="rmacl-joined">
                      <span className={cls.TeacherJoin ? 'is-yes' : ''} title="Teacher join">
                        <FontAwesomeIcon icon={cls.TeacherJoin ? faCheck : faXmark} /> TJ{cls.TeacherJoin ? ` ${fmtTime(new Date(cls.TeacherJoin))}` : ''}
                      </span>
                      <span className={cls.StudentJoin ? 'is-yes' : ''} title="Student join">
                        <FontAwesomeIcon icon={cls.StudentJoin ? faCheck : faXmark} /> SJ{cls.StudentJoin ? ` ${fmtTime(new Date(cls.StudentJoin))}` : ''}
                      </span>
                    </div>
                  </div>

                  <div className="rmacl-side">
                    <span className="rmacl-date">{fmtDay(cls.classDateTime)}</span>
                    <span className="rmacl-time">{fmtTime(cls.classDateTime)}</span>
                    <span className="rmacl-when">
                      {isLive && <b className="rmacl-now">Now</b>}
                      {!isLive && minsTo > 0 && minsTo <= 120 && <>in {minsTo} min</>}
                      {city && (
                        <span className="rmacl-their">
                          {(isLive || (minsTo > 0 && minsTo <= 120)) && ' · '}
                          {studentTime(cls.classDateTime, cls.timezone)} {city}
                        </span>
                      )}
                    </span>
                    {joinBtn('is-side')}
                  </div>
                </div>

                {/* ---- projects + contact ---- */}
                <div className="rmacl-mid">
                  {cls.type === 'paid' ? (
                    <>
                      <div className="rmacl-project">
                        <span className="rmacl-label">Previous</span>
                        <strong>{cls.prevProject}</strong>
                        <div className="rmacl-links">
                          {cls.lastCourseLink && (
                            <a href={cls.lastCourseLink} target="_blank" rel="noopener noreferrer" title="Project" aria-label="Project"><FontAwesomeIcon icon={faPlayCircle} /><span className="rmacl-link-text">Project</span></a>
                          )}
                          {cls.projectLink && (
                            <a href={cls.projectLink} target="_blank" rel="noopener noreferrer" title="Course" aria-label="Course"><FontAwesomeIcon icon={faBook} /><span className="rmacl-link-text">Course</span></a>
                          )}
                          {cls.courseNotes && (
                            <button type="button" title="Note" aria-label="Note" onClick={() => info('Teacher class notes', cls.courseNotes.trim() || 'No notes for this class.')}>
                              <FontAwesomeIcon icon={faStickyNote} /><span className="rmacl-link-text">Note</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="rmacl-project">
                        <span className="rmacl-label">Next</span>
                        <strong>{cls.nextProject}</strong>
                        <div className="rmacl-links">
                          {cls.nextCourseLink && (
                            <a href={cls.nextCourseLink} target="_blank" rel="noopener noreferrer" title="Project" aria-label="Project"><FontAwesomeIcon icon={faPlayCircle} /><span className="rmacl-link-text">Project</span></a>
                          )}
                          {cls.nextprojectLink && (
                            <a href={cls.nextprojectLink} target="_blank" rel="noopener noreferrer" title="Course" aria-label="Course"><FontAwesomeIcon icon={faBook} /><span className="rmacl-link-text">Course</span></a>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    (() => {
                      const demo = demoForCourse(demos, cls.course, cls.plan);
                      return (
                        <div className="rmacl-project">
                          <span className="rmacl-label">{cls.course ? `Asked for ${cls.course}` : 'Projects'}</span>
                          <strong>{demo ? `Demo: ${demo.name}` : cls.course ? 'No demo for this course yet' : 'Demo projects (course not chosen)'}</strong>
                          {demo?.link && (
                            <div className="rmacl-links">
                              <a href={demo.link} target="_blank" rel="noopener noreferrer" title="Demo project" aria-label="Demo project"><FontAwesomeIcon icon={faPlayCircle} /><span className="rmacl-link-text">Demo</span></a>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}

                  {cls.MobileNumber && (
                    <div className="rmacl-contact">
                      <span className="rmacl-label">Parent</span>
                      <div className="rmacl-links">
                        <a className="is-wa" href={waUrl(cls.MobileNumber)} target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faWhatsapp} /> Chat</a>
                        <button type="button" className="is-wa" onClick={() => remind(cls, false)}><FontAwesomeIcon icon={faBell} /> Remind</button>
                        <button type="button" className="is-wa" onClick={() => remind(cls, true)}><FontAwesomeIcon icon={faClock} /> Late</button>
                      </div>
                    </div>
                  )}
                  {joinBtn('is-mid')}
                </div>

                {/* ---- zoom ---- */}
                <div className="rmacl-zoom">
                  <span className="rmacl-label">Zoom</span>
                  {cls.hasZoomLink ? (
                    cls.meetingTimeMatch ? (
                      <span className="rmacl-pill tone-green"><FontAwesomeIcon icon={faCheck} /> Time OK</span>
                    ) : (
                      <span className="rmacl-pill tone-red"><FontAwesomeIcon icon={faXmark} /> Wrong time</span>
                    )
                  ) : (
                    <span className="rmacl-pill tone-grey">No link</span>
                  )}
                  {cls.hasZoomLink && (
                    <span
                      className="rmacl-host"
                      style={{ background: hostColor, color: inkOn(hostColor) }}
                      title={cls.userNumber ? `User ${cls.userNumber} – ${cls.hostEmail || ''}` : 'Host not detected'}
                    >
                      {cls.userNumber ? `U${cls.userNumber}` : '?'}
                    </span>
                  )}
                  {cls.conflict && (
                    <span className="rmacl-pill tone-red" title={cls.conflictMessage}><FontAwesomeIcon icon={faTriangleExclamation} /> Conflict</span>
                  )}
                  {cls.potentialConflict && !cls.conflict && cls.suggestion && (
                    <span className="rmacl-pill tone-orange" title={cls.conflictMessage}><FontAwesomeIcon icon={faHourglassHalf} /> Delay risk</span>
                  )}
                  {(cls.conflict || cls.potentialConflict) && cls.suggestion && (
                    <button
                      type="button"
                      className="rmacl-host is-btn"
                      style={{ background: userColor(cls.suggestion.number), color: inkOn(userColor(cls.suggestion.number)) }}
                      onClick={() => showSuggestion(cls)}
                      title={`Suggestion: use User ${cls.suggestion.number}`}
                    >
                      <FontAwesomeIcon icon={faArrowRight} /> U{cls.suggestion.number}
                    </button>
                  )}

                  <span className="rmacl-grow" />

                  {cls.hasZoomLink && !cls.meetingTimeMatch && (
                    <button type="button" className="rmacl-mini is-red" onClick={() => fixMeeting(cls)} disabled={busy[`fix-${cls.id}`]}>
                      <FontAwesomeIcon icon={faWrench} /> {busy[`fix-${cls.id}`] ? 'Fixing…' : 'Fix time'}
                    </button>
                  )}
                  {!cls.hasZoomLink && (
                    <button type="button" className="rmacl-mini is-green" onClick={() => createMeeting(cls)} disabled={busy[`create-${cls.IdUser}`]}>
                      <FontAwesomeIcon icon={faPlus} /> {busy[`create-${cls.IdUser}`] ? 'Creating…' : 'Create meeting'}
                    </button>
                  )}
                  {cls.hasZoomLink && (
                    <button type="button" className="rmacl-mini" onClick={() => showZoomInfo(cls)} aria-label="Meeting ID and password">
                      <FontAwesomeIcon icon={faEye} /> Details
                    </button>
                  )}
                </div>

                {/* ---- report issue (opens on hover / focus, same as teacher) ---- */}
                <div className="rmacl-report">
                  <div className="rmacl-report-inner">
                    <div className="rmacl-report-head">
                      <span className="rmacl-label">Report issue</span>
                      {!reportOpen && <small>Available during the class</small>}
                    </div>
                    <div className="rmacl-report-btns">
                      {ISSUES.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          className={cls.Issue === it.id ? 'is-on' : ''}
                          onClick={() => report(cls, it.id)}
                          disabled={!reportOpen || busy[`issue-${cls.id}`]}
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} /> {it.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`is-resolve ${cls.Issue === 0 ? 'is-on' : ''}`}
                        onClick={() => report(cls, 0)}
                        disabled={!reportOpen || busy[`issue-${cls.id}`]}
                      >
                        <FontAwesomeIcon icon={faCircleCheck} /> Issue resolved
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* ---------- Dialog (replaces the old innerHTML pop-ups) ---------- */}
      {dialog && (
        <div className="rmacl-overlay" onClick={() => setDialog(null)}>
          <div className={`rmacl-dialog is-${dialog.tone || 'info'}`} role="dialog" aria-modal="true" aria-label={dialog.title} onClick={(e) => e.stopPropagation()}>
            <span className="rmacl-dialog-icon">
              <FontAwesomeIcon
                icon={dialog.tone === 'success' ? faCircleCheck : dialog.tone === 'error' ? faTriangleExclamation : dialog.tone === 'warn' ? faClock : faCircleInfo}
              />
            </span>
            <h2>{dialog.title}</h2>
            {typeof dialog.body === 'string' ? <p className="rmacl-dialog-text">{dialog.body}</p> : dialog.body}
            {dialog.copied && <p className="rmacl-copied"><FontAwesomeIcon icon={faCheck} /> {dialog.copied}</p>}
            <div className="rmacl-dialog-actions">
              {(dialog.actions || []).map((a) =>
                a.href ? (
                  <a key={a.label} className={`rmacl-btn rmacl-btn--ghost ${a.wa ? 'is-wa' : ''}`} href={a.href} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={a.icon} /> {a.label}
                  </a>
                ) : (
                  <button key={a.label} type="button" className="rmacl-btn rmacl-btn--ghost" onClick={a.onClick}>
                    <FontAwesomeIcon icon={a.icon} /> {a.label}
                  </button>
                )
              )}
              <button type="button" className="rmacl-btn" onClick={() => setDialog(null)} autoFocus>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classes;